import { useMemo } from 'react';
import { useContainersByUris } from 'src/hooks/services/useContainer';
import { useLotsByUris } from 'src/hooks/services/useLot';
import { API_RESOURCES } from 'src/utils/constants';
import { getResourceUri, getUuid } from 'src/utils/url';

/**
 * Resolves material lot display names keyed by container UUID.
 *
 * Pass already-loaded `containers` when available to avoid re-fetching.
 * Otherwise containers are loaded from `containerUuids`.
 */
const useMaterialLotNamesByContainers = ({ containerUuids = [], containers = null } = {}) => {
  const shouldFetchContainers = !containers && containerUuids.length > 0;

  const containerUris = useMemo(
    () =>
      containerUuids
        .filter(Boolean)
        .map(uuid => getResourceUri(API_RESOURCES.MATERIAL_CONTAINER, uuid)),
    [containerUuids]
  );

  const { data: containersByUri, isLoading: isContainersLoading } = useContainersByUris(
    shouldFetchContainers ? containerUris : [],
    'material-lot-names-by-containers'
  );

  const resolvedContainers = useMemo(() => {
    if (containers) {
      return containers;
    }
    return Object.values(containersByUri ?? {});
  }, [containers, containersByUri]);

  const lotUriByContainerUuid = useMemo(
    () =>
      resolvedContainers.reduce((acc, container) => {
        const uuid = getUuid(container.uri);
        if (uuid && container.material_lot) {
          acc[uuid] = container.material_lot;
        }
        return acc;
      }, {}),
    [resolvedContainers]
  );

  const materialLotUris = useMemo(
    () => [...new Set(Object.values(lotUriByContainerUuid).filter(Boolean))],
    [lotUriByContainerUuid]
  );

  const { data: materialLots = [], isLoading: isLotsLoading } = useLotsByUris(
    materialLotUris,
    'material-lot-names'
  );

  const namesByUuid = useMemo(
    () =>
      containerUuids.reduce((acc, uuid) => {
        const lotUri = lotUriByContainerUuid[uuid];
        const lotName = materialLots.find(lot => lot.uri === lotUri)?.name;
        if (lotName) {
          acc[uuid] = lotName;
        }
        return acc;
      }, {}),
    [containerUuids, lotUriByContainerUuid, materialLots]
  );

  return {
    namesByUuid,
    isLoading:
      (shouldFetchContainers && isContainersLoading) ||
      (materialLotUris.length > 0 && isLotsLoading),
  };
};

export default useMaterialLotNamesByContainers;
